import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import {
  formatCheckinWhenLine,
  type CheckinOccurrenceRow,
} from '@/pages/attendance/checkin-occurrence-utils';
import { OpenCheckinDialog } from '@/pages/attendance/open-checkin-dialog';
import type { CheckinGatheringChoice } from '@/pages/attendance/checkin-calendar-gatherings';
import {
  readCheckinFilterByScheduleTime,
  writeCheckinFilterByScheduleTime,
} from '@/pages/attendance/checkin-schedule-filter';
import { KioskScreenLink } from '@/pages/attendance/kiosk-screen-link';
import { useCheckinGatherings } from '@/pages/attendance/use-checkin-gatherings';
import { usePreferences } from '@/stores/preferences-store';
import { cn } from '@/utils/cn';
import { churchToday } from '@/utils/format';

interface CheckinOccurrence extends CheckinOccurrenceRow {
  status: string;
}

interface AttendanceSession {
  id: string;
  occurrenceId: string;
  method: string;
  status: string;
  headcount: number;
  kiosks: Array<{ id: string; name: string; pairingUrl: string }>;
}

interface LiveSessionCardProps {
  session: AttendanceSession;
  locale: string;
  kindLabel: (kind: 'service' | 'program') => string;
  multiDayRangeLabel: (start: string, end: string) => string;
  onClose: () => void;
}

const LiveSessionCard = ({
  session,
  locale,
  kindLabel,
  multiDayRangeLabel,
  onClose,
}: LiveSessionCardProps) => {
  const { t } = useTranslation('attendance');
  const { t: tCommon } = useTranslation();

  const occurrence = useQuery({
    queryKey: ['occurrences', session.occurrenceId],
    queryFn: () => api.get<CheckinOccurrence>(`/occurrences/${session.occurrenceId}`),
  });

  const occ = occurrence.data;
  const kind = occ?.programKind ?? 'service';
  const gatheringName = occ?.programName?.trim() || kindLabel(kind);

  return (
    <Card className="border-success/30 bg-success/5">
      <CardHeader className="gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          {occ ? (
            <>
              <CardTitle className="flex flex-wrap items-center gap-2">
                <span>{gatheringName}</span>
                <Badge variant="neutral">{kindLabel(kind)}</Badge>
              </CardTitle>
              <CardDescription>
                {formatCheckinWhenLine(occ, locale, multiDayRangeLabel)}
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle>{t('sessions.liveTitle')}</CardTitle>
              <CardDescription>
                {occurrence.isLoading ? tCommon('state.loading') : t('sessions.liveDescription')}
              </CardDescription>
            </>
          )}
        </div>
        <Badge variant="success">{t('sessions.statusOpen')}</Badge>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-body-lg font-semibold tabular-nums text-fg">
            {t('sessions.headcountLive', { count: session.headcount })}
          </p>
          {session.kiosks?.length ? (
            <div className="mt-2">
              <p className="text-body-sm font-medium text-fg">{t('sessions.detail.screensTitle')}</p>
              <ul className="mt-1 flex flex-wrap gap-2">
                {session.kiosks.map((screen) => (
                  <li key={screen.id}>
                    <KioskScreenLink id={screen.id} name={screen.name} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" asChild>
            <Link to={`/attendance/sessions/${session.id}`}>{t('sessions.viewSession')}</Link>
          </Button>
          <Button variant="outline" onClick={onClose}>
            {t('sessions.close')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const AttendanceCheckinPanel = ({ compact = false }: { compact?: boolean }) => {
  const { t } = useTranslation('attendance');
  const { t: tPrograms } = useTranslation('programs');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const { locale, timezone } = usePreferences();
  const queryClient = useQueryClient();

  const today = churchToday(timezone);

  const [closeTarget, setCloseTarget] = useState<AttendanceSession | null>(null);
  const [openTarget, setOpenTarget] = useState<CheckinGatheringChoice | null>(null);
  const [filterByScheduleTime, setFilterByScheduleTime] = useState(readCheckinFilterByScheduleTime);

  const kindLabel = (kind: 'service' | 'program') => tPrograms(`calendar.kind.${kind}`);

  const multiDayRangeLabel = (start: string, end: string) =>
    t('sessions.multiDayRange', { start, end });

  const sessions = useQuery({
    queryKey: ['attendance-sessions', 'open'],
    queryFn: () => api.get<{ data: AttendanceSession[] }>('/attendance/sessions?status=open&pageSize=20'),
    refetchInterval: 15_000,
  });

  const openSessions = useMemo(
    () => (sessions.data?.data ?? []).filter((row) => row.status === 'open' && row.method === 'qr'),
    [sessions.data?.data],
  );

  const { gatherings, gatheringsForDay, isLoading, invalidateKeys } = useCheckinGatherings(
    today,
    timezone,
    openSessions,
    filterByScheduleTime,
  );

  const loading = isLoading;
  const selectable = useMemo(() => gatherings, [gatherings]);
  const showStartCheckin = !loading && gatheringsForDay.length > 0;
  const noneMatchScheduleFilter =
    filterByScheduleTime && gatheringsForDay.length > 0 && selectable.length === 0;

  const closeSession = useMutation({
    mutationFn: (sessionId: string) => api.post<AttendanceSession>(`/attendance/sessions/${sessionId}/close`),
    onSuccess: () => {
      toast.success(t('sessions.closed'));
      setCloseTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['attendance-sessions'] });
      for (const key of invalidateKeys) {
        void queryClient.invalidateQueries({ queryKey: [...key] });
      }
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  return (
    <div className={cn('space-y-4', compact ? '' : 'mb-6')}>
      {openSessions.map((session) => (
        <LiveSessionCard
          key={session.id}
          session={session}
          locale={locale}
          kindLabel={kindLabel}
          multiDayRangeLabel={multiDayRangeLabel}
          onClose={() => setCloseTarget(session)}
        />
      ))}

      {loading ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-8">
            <Spinner />
            <span className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</span>
          </CardContent>
        </Card>
      ) : null}

      {!loading && selectable.length === 0 && openSessions.length === 0 && !showStartCheckin ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('sessions.noGatheringsTitle')}</CardTitle>
            <CardDescription>{t('sessions.noGatheringsDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" asChild>
              <Link to="/calendar">{t('sessions.openCalendar')}</Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {showStartCheckin ? (
        <Card>
          <CardHeader className="gap-3 space-y-0 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <CardTitle>{t('sessions.startTitle')}</CardTitle>
              <CardDescription>{t('sessions.startDescription')}</CardDescription>
            </div>
            <div className="flex shrink-0 items-center gap-2 pt-0.5">
              <label
                htmlFor="checkin-filter-by-schedule-time"
                className="cursor-pointer text-body-sm text-fg-secondary"
              >
                {t('sessions.filterByScheduleTime.label')}
              </label>
              <Switch
                id="checkin-filter-by-schedule-time"
                checked={filterByScheduleTime}
                onCheckedChange={(checked) => {
                  setFilterByScheduleTime(checked);
                  writeCheckinFilterByScheduleTime(checked);
                }}
                aria-label={t('sessions.filterByScheduleTime.hint')}
              />
            </div>
          </CardHeader>
          <CardContent>
            {noneMatchScheduleFilter ? (
              <p className="text-body-sm text-fg-secondary">{t('sessions.noneInScheduleWindow')}</p>
            ) : (
              <ul className="divide-y divide-border rounded-lg border border-border">
                {selectable.map((row) => {
                  const kind = row.programKind ?? 'service';
                  return (
                    <li
                      key={`${row.programId}:${row.occurrenceDate}`}
                      className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <span className="font-medium text-fg">
                          {row.programName ?? kindLabel(kind)}
                        </span>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <Badge variant="neutral">{kindLabel(kind)}</Badge>
                          <span className="text-body-sm text-fg-secondary">
                            {formatCheckinWhenLine(row, locale, multiDayRangeLabel)}
                          </span>
                        </div>
                      </div>
                      <div className="flex shrink-0 justify-end">
                        <Button size="sm" onClick={() => setOpenTarget(row)}>
                          {t('sessions.open')}
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      <OpenCheckinDialog
        open={openTarget !== null}
        onOpenChange={(next) => {
          if (!next) setOpenTarget(null);
        }}
        gathering={openTarget}
        kindLabel={kindLabel}
        locale={locale}
      />

      <ConfirmDialog
        open={closeTarget !== null}
        onOpenChange={(next) => {
          if (!next) setCloseTarget(null);
        }}
        title={t('confirm.closeSession.title')}
        description={t('confirm.closeSession.description')}
        confirmLabel={t('confirm.closeSession.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={closeSession.isPending}
        onConfirm={() => {
          if (closeTarget) closeSession.mutate(closeTarget.id);
        }}
      />
    </div>
  );
};
